import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../components/Panel'
import { StatusTag } from '../../../../components/tableTemplates'
import { usePanelContext } from '../../../../contexts/panel'
import { useToastContext } from '../../../../contexts/toast'
import { WebEdiDto } from '../../../../data/models'
import { getHandleableError } from '../../../../utils/errors/isXiaError'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../utils/helper/validateFormikFields'
import i18n from '../../../../utils/i18n'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import {
  DeliveryConfirmationDto,
  DeliveryConfirmationPatchDto,
} from '../../data/model'

interface Props {
  data?: DeliveryConfirmationDto
  onSubmit: (data: DeliveryConfirmationPatchDto) => Promise<void>
  id: WebEdiDto['Id']
  isLoading: boolean
}

interface FormValues extends DeliveryConfirmationDto {}

type FormFieldName = keyof DeliveryConfirmationDto

export const DeliveryConfirmation: React.FC<Props> = ({
  data,
  onSubmit,
  id,
  isLoading,
}) => {
  const [isEnableErrorMsg, setIsEnableErrorMsg] = useState<boolean>(false)

  const { t } = useTranslation()
  const { language } = i18n

  const handleSubmit =
    onSubmit ||
    (async () => {
      return
    })

  const panelId = 'despatchAdvice-deliveryConfirmation'
  const { isEdit } = usePanelContext()

  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { ...data },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'deliveryNoteNumber',
          config: config.despatchAdvice.deliveryConfirmation.deliveryNoteNumber,
        },
        {
          fieldName: 'deliveryDate',
          config: config.despatchAdvice.deliveryConfirmation.deliveryDate,
        },
        {
          fieldName: 'gln',
          config: config.despatchAdvice.deliveryConfirmation.gln,
        },
        {
          fieldName: 'company',
          config: config.despatchAdvice.deliveryConfirmation.company,
        },
        {
          fieldName: 'department',
          config: config.despatchAdvice.deliveryConfirmation.department,
        },
        {
          fieldName: 'name',
          config: config.despatchAdvice.deliveryConfirmation.name,
        },
        {
          fieldName: 'phone',
          config: config.despatchAdvice.deliveryConfirmation.phone,
        },
        {
          fieldName: 'email',
          config: config.despatchAdvice.deliveryConfirmation.email,
        },
        {
          fieldName: 'notes',
          config: config.despatchAdvice.deliveryConfirmation.notes,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: data => {
      try {
        // send data in DespatchAdviceData
        handleSubmit(data)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'despatchAdvice.deliveryConfirmation.edit.save.messages.success.summary',
          ),
          detail: t(
            'despatchAdvice.deliveryConfirmation.edit.save.messages.success.detail',
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'despatchAdvice.deliveryConfirmation.edit.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      }
    },
  })

  const showError = (name: FormFieldName) => {
    const hasEmailError = formik.errors.email !== undefined
    const hasDeliveryNoteNumberError =
      formik.errors.deliveryNoteNumber !== undefined
    const hasPhoneError = formik.errors.phone !== undefined

    return hasEmailError ||
      hasDeliveryNoteNumberError ||
      hasPhoneError ||
      (hasEmailError && hasDeliveryNoteNumberError && hasPhoneError)
      ? !!formik.errors[name]
      : !!(formik.touched[name] && formik.errors[name])
  }

  const isFormFieldValid = (name: FormFieldName) => showError(name)
  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  useEffect(() => {
    if (
      formik.errors.deliveryNoteNumber !== undefined ||
      formik.errors.email !== undefined ||
      formik.errors.phone !== undefined
    ) {
      setIsEnableErrorMsg(true)
    } else {
      setIsEnableErrorMsg(false)
    }
  }, [
    isEnableErrorMsg,
    formik.errors.deliveryNoteNumber,
    formik.errors.email,
    formik.errors.phone,
  ])

  const movedComponentIfErrorMsgEnable = (msgIdText: any) => {
    return isEnableErrorMsg ? (
      <div style={{ minHeight: '2.5em' }}>{getFormErrorMessage(msgIdText)}</div>
    ) : (
      <div>{getFormErrorMessage(msgIdText)}</div>
    )
  }

  // ************************************************************
  // components
  // ************************************************************
  // deliveryNoteNumber
  const deliveryNoteNumberLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('deliveryNoteNumber'),
      })}
      htmlFor="deliveryNoteNumber"
    ></label>
  )

  const deliveryNoteNumberComponent = (
    <InputText
      id="deliveryNoteNumber"
      value={formik.values.deliveryNoteNumber}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('deliveryNoteNumber'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const deliveryNoteNumberErrorMsg =
    movedComponentIfErrorMsgEnable('deliveryNoteNumber')

  const deliveryNoteNumberWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={deliveryNoteNumberComponent}
      label={deliveryNoteNumberLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryNoteNumberErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryDate
  const transformedDeliveryDate: Date = new Date(
    Date.parse(formik.values.deliveryDate!),
  )

  const deliveryDateLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('deliveryDate'),
      })}
      htmlFor="deliveryDate"
    ></label>
  )

  const deliveryDateComponent = (
    <Calendar
      id="deliveryDate"
      value={transformedDeliveryDate}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('deliveryDate'),
      })}
      readOnlyInput
      showButtonBar
      showIcon
      minDate={new Date()}
      locale={language}
      disabled={!isEdit(panelId)}
    />
  )

  const deliveryDateErrorMsg = movedComponentIfErrorMsgEnable('deliveryDate')

  const deliveryDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={deliveryDateComponent}
      label={deliveryDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryDateErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryType
  const deliveryTypeLabel = (
    <label className="text-sm" htmlFor="deliveryType"></label>
  )

  const deliveryTypeComponent = (
    <InputText
      id="deliveryType"
      value={
        data?.deliveryType === 'FULL'
          ? t('common.codes.deliveryType.full')
          : t('common.codes.deliveryType.partly')
      }
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('deliveryType'),
      })}
      disabled
    />
  )

  const deliveryTypeErrorMsg = movedComponentIfErrorMsgEnable('deliveryType')

  const deliveryTypeWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={deliveryTypeComponent}
      label={deliveryTypeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryTypeErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationCode
  const confirmationCodeErrorMsg =
    movedComponentIfErrorMsgEnable('confirmationCode')

  const confirmationCodeLabel = (
    <label className="text-sm w-full" htmlFor="confirmationCode"></label>
  )

  const confirmationCodeComponent = (
    <StatusTag
      id="confirmationCode"
      status={data?.confirmationCode}
      className="p-2"
    />
  )

  const confirmationCodeWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={confirmationCodeComponent}
      label={confirmationCodeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationCodeErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationId
  const confirmationIdLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationId'),
      })}
      htmlFor="confirmationId"
    ></label>
  )

  const confirmationIdComponent = (
    <InputText
      id="confirmationId"
      value={formik.values.confirmationId}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationId'),
      })}
      disabled
    />
  )

  const confirmationIdErrorMsg =
    movedComponentIfErrorMsgEnable('confirmationId')

  const confirmationIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={confirmationIdComponent}
      label={confirmationIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationDate
  const transformedConfirmationDate: Date = new Date(
    Date.parse(data?.confirmationDate!),
  )

  const confirmationDateLabel = (
    <label className="text-sm" htmlFor="confirmationDate"></label>
  )

  const confirmationDateComponent = (
    <Calendar
      id="confirmationDate"
      value={transformedConfirmationDate}
      className="w-full"
      readOnlyInput
      showIcon
      locale={language}
      disabled
    />
  )

  const confirmationDateErrorMsg =
    movedComponentIfErrorMsgEnable('confirmationDate')

  const confirmationDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={confirmationDateComponent}
      label={confirmationDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationDateErrorMsg}
      isLoading={isLoading}
    />
  )

  // gln
  const glnLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('gln'),
      })}
      htmlFor="gln"
    ></label>
  )

  const glnComponent = (
    <InputText
      id="gln"
      value={formik.values.gln}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('gln'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const glnErrorMsg = movedComponentIfErrorMsgEnable('gln')

  const glnWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={glnComponent}
      label={glnLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={glnErrorMsg}
      isLoading={isLoading}
    />
  )

  // company
  const companyLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('company'),
      })}
      htmlFor="company"
    ></label>
  )

  const companyComponent = (
    <InputText
      id="company"
      value={formik.values.company}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('company'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const companyErrorMsg = movedComponentIfErrorMsgEnable('company')

  const companyWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={companyComponent}
      label={companyLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={companyErrorMsg}
      isLoading={isLoading}
    />
  )

  // department
  const departmentLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('department'),
      })}
      htmlFor="department"
    ></label>
  )

  const departmentComponent = (
    <InputText
      id="department"
      value={formik.values.department}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('department'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const departmentErrorMsg = movedComponentIfErrorMsgEnable('department')

  const departmentWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={departmentComponent}
      label={departmentLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={departmentErrorMsg}
      isLoading={isLoading}
    />
  )

  // name
  const nameLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('name'),
      })}
      htmlFor="name"
    ></label>
  )

  const nameComponent = (
    <InputText
      id="name"
      value={formik.values.name}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('name'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const nameErrorMsg = movedComponentIfErrorMsgEnable('name')

  const nameWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={nameComponent}
      label={nameLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={nameErrorMsg}
      isLoading={isLoading}
    />
  )

  // phone
  const phoneLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('phone'),
      })}
      htmlFor="phone"
    ></label>
  )

  const phoneComponent = (
    <InputText
      id="phone"
      value={formik.values.phone}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('phone'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const phoneErrorMsg = movedComponentIfErrorMsgEnable('phone')

  const phoneWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={phoneComponent}
      label={phoneLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={phoneErrorMsg}
      isLoading={isLoading}
    />
  )

  // email
  const emailLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('email'),
      })}
      htmlFor="email"
    ></label>
  )

  const emailComponent = (
    <InputText
      id="email"
      value={formik.values.email}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('email'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const emailErrorMsg = movedComponentIfErrorMsgEnable('email')

  const emailWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={emailComponent}
      label={emailLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={emailErrorMsg}
      isLoading={isLoading}
    />
  )
  // notes
  const notesLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('notes'),
      })}
      htmlFor="notes"
    ></label>
  )

  const notesComponent = (
    <InputTextarea
      id="notes"
      value={formik.values.notes}
      onChange={formik.handleChange}
      rows={5}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('notes'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const notesErrorMsg = <div>{getFormErrorMessage('notes')}</div>

  const notesWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.deliveryConfirmation}
      component={notesComponent}
      label={notesLabel}
      className="col-12"
      errorMsg={notesErrorMsg}
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      formik={formik}
      header={getConfigTranslation(
        config.despatchAdvice.deliveryConfirmation.title,
      )}
      id={panelId}
      visible={config.despatchAdvice.deliveryConfirmation.enabled}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="formgrid grid">
          {/* deliveryNoteNumber */}
          {deliveryNoteNumberWrapper}

          {/* deliveryDate */}
          {deliveryDateWrapper}

          {/* deliveryType */}
          {deliveryTypeWrapper}

          {/* confirmationCode */}
          {confirmationCodeWrapper}

          {/* confirmationId */}
          {confirmationIdWrapper}

          {/* confirmationDate */}
          {confirmationDateWrapper}

          {/* gln */}
          {glnWrapper}

          {/* company */}
          {companyWrapper}

          {/* department */}
          {departmentWrapper}

          {/* name */}
          {nameWrapper}

          {/* phone */}
          {phoneWrapper}

          {/* email */}
          {emailWrapper}

          {/* notes */}
          {notesWrapper}
        </div>
      </form>
    </PanelContainer>
  )
}
